import React from "react";
import { Button, ButtonProps, CircularProgress, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { lightTheme } from "../../styles/theme";

const primaryHoverColor = lightTheme.colors.primaryAccent;

interface NoticeboardButtonProps
  extends Omit<ButtonProps, "variant" | "color" | "size"> {
  variant?: "primary" | "secondary" | "tertiary" | "danger";
  size?:
    | "small"
    | "medium"
    | "large"
    | { xs: "small" | "medium" | "large"; sm: "small" | "medium" | "large" };
  loading?: boolean;
  /** Link attributes when href renders this button as an anchor. */
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  children: React.ReactNode;
}

const NoticeboardButton: React.FC<NoticeboardButtonProps> = ({
  variant = "primary",
  size = "medium",
  loading = false,
  children,
  sx,
  ...buttonProps
}) => {
  const theme = useTheme();

  const getSizeStyles = () => {
    // Handle responsive sizing
    if (typeof size === "object") {
      const mobileSize = size.xs;
      const desktopSize = size.sm;

      const getSizeConfig = (sizeKey: "small" | "medium" | "large") => {
        switch (sizeKey) {
          case "small":
            return {
              minWidth: 56,
              px: 1,
              py: 0.5,
              height: 28,
              fontSize: "0.75rem",
            };
          case "large":
            return {
              minWidth: 140,
              px: 4,
              py: 2,
              height: 56,
              fontSize: "1rem",
            };
          default: // medium
            return {
              minWidth: 120,
              px: 2,
              py: 0.75,
              height: "auto",
              fontSize: theme.typography.body2.fontSize,
            };
        }
      };

      const mobileConfig = getSizeConfig(mobileSize);
      const desktopConfig = getSizeConfig(desktopSize);

      return {
        minWidth: { xs: mobileConfig.minWidth, sm: desktopConfig.minWidth },
        px: { xs: mobileConfig.px, sm: desktopConfig.px },
        py: { xs: mobileConfig.py, sm: desktopConfig.py },
        height: { xs: mobileConfig.height, sm: desktopConfig.height },
        fontSize: { xs: mobileConfig.fontSize, sm: desktopConfig.fontSize },
      };
    }

    // Handle fixed sizing
    switch (size) {
      case "small":
        return {
          minWidth: 56,
          px: 1,
          py: 0.5,
          height: 28,
          fontSize: "0.75rem",
        };
      case "large":
        return {
          minWidth: 140,
          px: 4,
          py: 2,
          height: 56,
          fontSize: "1rem",
        };
      default: // medium
        return {
          minWidth: 120,
          px: 2,
          py: 0.75,
          height: "auto",
          fontSize: theme.typography.body2.fontSize,
        };
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          border: `1px solid ${theme.palette.primary.main}`,
          "&:hover": {
            backgroundColor: primaryHoverColor,
            borderColor: primaryHoverColor,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
      case "secondary":
        return {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          "&:hover": {
            backgroundColor: theme.palette.primary.light + "10",
            borderColor: primaryHoverColor,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
      case "tertiary":
        return {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.common.white,
          border: `1px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            backgroundColor: theme.palette.secondary.dark,
            borderColor: theme.palette.secondary.dark,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 12px ${theme.palette.secondary.main}40`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
      case "danger":
        return {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.common.white,
          border: `1px solid ${theme.palette.error.main}`,
          "&:hover": {
            backgroundColor: theme.palette.error.dark,
            borderColor: theme.palette.error.dark,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
      default:
        return {};
    }
  };

  return (
    <Button
      variant="contained"
      disabled={buttonProps.disabled || loading}
      sx={{
        textTransform: "none",
        fontWeight: 500,
        borderRadius: 1,
        boxShadow: "none",
        transition: theme.transitions.create(
          ["background-color", "border-color", "transform", "box-shadow"],
          {
            duration: theme.transitions.duration.short,
          }
        ),
        "&:disabled": {
          bgcolor: "action.disabledBackground",
          color: "action.disabled",
          border: "1px solid transparent",
          transform: "none",
          boxShadow: "none",
        },
        ...getSizeStyles(),
        ...getVariantStyles(),
        ...sx,
      }}
      {...buttonProps}
    >
      {loading ? (
        <CircularProgress size={18} sx={{ color: "currentColor" }} />
      ) : (
        children
      )}
    </Button>
  );
};

/** Primary CTAs in ActionModal footers: larger tap target on xs, compact from sm up. */
export const ACTION_MODAL_FOOTER_PRIMARY_SIZE = {
  xs: "large",
  sm: "medium",
} as const;

export default NoticeboardButton;
